<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  STATUS_LABELS,
  STATUS_TAG,
  createRoute,
  deleteRoute,
  listRoutes,
  updateRoute,
  type Route,
  type RouteInput,
} from '../api/routes'

const rows = ref<Route[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const query = reactive({ keyword: '', status: '', page: 1, size: 20 })

const formOpen = ref(false)
const editing = ref<Route | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive({
  code: '',
  name: '',
  origin: '',
  dest: '',
  distance_km: 0,
  est_minutes: 0,
  status: 'active' as Route['status'],
})

const codeError = computed(() => (form.code.trim() ? '' : '请输入线路编码'))
const originError = computed(() => (form.origin.trim() ? '' : '请填写起点'))
const destError = computed(() => (form.dest.trim() ? '' : '请填写终点'))
const formErrorText = computed(() => codeError.value || originError.value || destError.value)

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listRoutes({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      status: query.status || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => void load())

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function resetForm(): void {
  Object.assign(form, {
    code: '',
    name: '',
    origin: '',
    dest: '',
    distance_km: 0,
    est_minutes: 0,
    status: 'active' as Route['status'],
  })
}

function openCreate(): void {
  editing.value = null
  resetForm()
  formError.value = null
  formOpen.value = true
}

function openEdit(row: Route): void {
  editing.value = row
  Object.assign(form, {
    code: row.code,
    name: row.name,
    origin: row.origin,
    dest: row.dest,
    distance_km: row.distance_km,
    est_minutes: row.est_minutes,
    status: row.status,
  })
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input: RouteInput = {
    code: form.code.trim(),
    name: form.name.trim(),
    origin: form.origin.trim(),
    dest: form.dest.trim(),
    distance_km: Number(form.distance_km) || 0,
    est_minutes: Number(form.est_minutes) || 0,
    status: form.status,
  }
  try {
    if (editing.value) await updateRoute(editing.value.id, input)
    else await createRoute(input)
    formOpen.value = false
    await load(editing.value ? '线路已更新' : '线路已创建')
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function remove(row: Route): Promise<void> {
  notice.value = ''
  error.value = null
  try {
    await deleteRoute(row.id)
    await load(`已删除线路「${row.code}」`)
  } catch (e) {
    error.value = e
  }
}
</script>

<template>
  <div class="yc-rt">
    <header class="yc-rt__bar">
      <el-input
        v-model="query.keyword"
        class="yc-rt__keyword"
        placeholder="编码 / 名称 / 起讫地"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.status" class="yc-rt__status" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'dsp:route:create'" type="primary" @click="openCreate">新建线路</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-rt__notice">{{ notice }}</span>
    </header>

    <div v-if="error" class="yc-rt__error">
      <TraceIdAlert :error="error" />
    </div>

    <el-card v-loading="loading" class="yc-rt__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="code" label="线路编码" min-width="120" />
        <el-table-column prop="name" label="名称" min-width="170" show-overflow-tooltip />
        <el-table-column prop="origin" label="起点" min-width="160" show-overflow-tooltip />
        <el-table-column prop="dest" label="终点" min-width="160" show-overflow-tooltip />
        <el-table-column label="里程（km）" width="110" align="right">
          <template #default="{ row }">{{ Number(row.distance_km).toFixed(2) }}</template>
        </el-table-column>
        <el-table-column label="预计时长" width="110" align="right">
          <template #default="{ row }">{{ row.est_minutes }} 分钟</template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as Route['status']] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as Route['status']] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right">
          <template #default="{ row }">
            <el-button v-perm="'dsp:route:update'" link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-popconfirm title="确认删除该线路？已被行车任务引用的线路不能删除。" width="280" @confirm="remove(row)">
              <template #reference>
                <el-button v-perm="'dsp:route:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有线路"
        description="固定班线给行车任务用，先建一条起点到终点的线路。"
      >
        <el-button v-perm="'dsp:route:create'" type="primary" @click="openCreate">新建线路</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-rt__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑线路 · ${editing.code}` : '新建线路'"
      width="540px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-rt__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <div class="yc-rt__formrow">
          <el-form-item label="线路编码" :error="codeError">
            <el-input v-model="form.code" maxlength="32" placeholder="如 XL001" />
          </el-form-item>

          <el-form-item label="名称">
            <el-input v-model="form.name" maxlength="64" placeholder="如 上海—杭州干线" />
          </el-form-item>
        </div>

        <div class="yc-rt__formrow">
          <el-form-item label="起点" :error="originError">
            <el-input v-model="form.origin" maxlength="255" placeholder="如 上海青浦仓" />
          </el-form-item>

          <el-form-item label="终点" :error="destError">
            <el-input v-model="form.dest" maxlength="255" placeholder="如 杭州余杭仓" />
          </el-form-item>
        </div>

        <div class="yc-rt__formrow">
          <el-form-item label="里程（km）">
            <el-input-number
              v-model="form.distance_km"
              :min="0"
              :max="999999"
              :precision="2"
              controls-position="right"
            />
          </el-form-item>

          <el-form-item label="预计时长（分钟）">
            <el-input-number v-model="form.est_minutes" :min="0" :max="99999" controls-position="right" />
          </el-form-item>

          <el-form-item label="状态">
            <el-radio-group v-model="form.status">
              <el-radio value="active">启用</el-radio>
              <el-radio value="disabled">停用</el-radio>
            </el-radio-group>
          </el-form-item>
        </div>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          {{ editing ? '保存' : '创建' }}
        </el-button>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-rt {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 200px;
  }

  &__status {
    width: 140px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__formrow {
    display: flex;
    flex-wrap: wrap;
    gap: var(--yc-space-3);

    .el-form-item {
      margin-bottom: var(--yc-space-3);
    }
  }
}
</style>
